// services/prescriptionService.ts

export interface DrugDetails {
  drug_number?: string;
  laboratory?: string;
  country_of_origin?: string;
  registration_date?: string;
  drug_type?: string;
  common_side_effects?: string;
  possible_side_effects?: string;
  drug_interactions?: string;
  warnings?: string;
  alternatives?: string[];
  drug_brands?: string[];
  attachments?: Array<{
    name: string;
    url: string;
    type: string;
    size?: number;
  }>;
  is_prescribed?: boolean;
  has_safety_warnings?: boolean;
  safety_info?: {
    warnings?: string;
    drug_interactions?: string;
    common_side_effects?: string;
    possible_side_effects?: string;
  };
}

export interface PrescriptionHint {
  id: number;
  text: string;
  category: 'frequency' | 'dosage' | 'instructions' | 'timing';
  language: 'en' | 'ar' | 'fr';
}

export interface Medicine {
  id: number;
  name: string;
  generic_name?: string;
  strength?: string;
  form: 'tablet' | 'capsule' | 'syrup' | 'injection' | 'cream' | 'drops' | 'inhaler' | 'suppository' | 'patch' | 'gel' | 'powder' | 'solution';
  manufacturer?: string;
  category?: string;
  description?: string;
  side_effects?: string;
  contraindications?: string;
  price?: number;
  is_active: boolean;
  display_name?: string;
  drug_details?: DrugDetails | null;
  statistics?: {
    total_prescriptions: number;
    active_prescriptions: number;
    last_prescribed?: string;
  };
}

export interface PrescriptionItem {
  id?: number;
  medicine_id: number; // Made optional and nullable
  medicine?: Medicine;
  dosage?: string;
  frequency?: string;
  duration?: string;
  instructions?: string;
  doctor_notes?: string;
  quantity?: number;
  before_after_meal?: 'before' | 'after' | 'with' | 'anytime';
  prescription_hint_id?: number;
  prescriptionHint?: PrescriptionHint;
  custom_instructions?: string;
  start_date?: string;
  end_date?: string;
}

export interface Prescription {
  id?: number;
  patient_id: number;
  patient?: {
    id: number;
    full_name: string;
    first_name: string;
    last_name: string;
    birth_date: string;
    age: number;
    phone: string;
    gender: 'male' | 'female';
    allergies?: string[];
    chronic_conditions?: string[];
  };
  doctor_id: number;
  doctor?: {
    id: number;
    name: string;
    first_name: string;
    last_name: string;
    specialty?: string;
  };
  appointment_id?: number;
  clinic_id: number;
  clinic?: {
    id: number;
    name: string;
    address: string;
    phone: string;
  };
  prescription_date: string;
  diagnosis?: string;
  doctor_notes?: string;
  status: 'active' | 'completed' | 'cancelled';
  whatsapp_sent: boolean;
  whatsapp_sent_at?: string;
  pdf_generated: boolean;
  pdf_path?: string;
  items: PrescriptionItem[];
  created_at?: string;
  updated_at?: string;
}
export interface QuickAddMedicineData {
  name: string;
  generic_name?: string;
  strength?: string;
  form: 'tablet' | 'capsule' | 'syrup' | 'injection' | 'cream' | 'drops' | 'inhaler' | 'suppository' | 'patch' | 'gel' | 'powder' | 'solution' | 'other';
  category?: string;
  manufacturer?: string;
}
export interface CreatePrescriptionData {
  patient_id: number;
  appointment_id?: number;
  prescription_date: string;
  diagnosis?: string;
  doctor_notes?: string;
  items: Array<{
    medicine_id: number;
    dosage?: string;
    frequency?: string;
    duration?: string;
    instructions?: string;
    doctor_notes?: string;
    quantity?: number;
    before_after_meal?: 'before' | 'after' | 'with' | 'anytime';
  }>;
}

export interface MedicineSearchParams {
  search?: string;
  category?: string;
  form?: string;
  manufacturer?: string;
  drug_type?: string;
  is_prescribed?: boolean;
  active_only?: boolean;
  exclude_drug_details?: boolean;
  page?: number;
  per_page?: number;
  sort_by?: 'name' | 'generic_name' | 'category' | 'form' | 'manufacturer' | 'price' | 'created_at';
  sort_order?: 'asc' | 'desc';
}

export interface DrugInteractionWarning {
  medicine_name: string;
  interaction_type: 'major' | 'moderate' | 'minor';
  description: string;
  severity: 'high' | 'medium' | 'low';
}

export interface LaravelPaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
  };
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
}

class PrescriptionService {
  private static BASE_URL = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api';

  private static async request(url: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('auth_token');
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(`${PrescriptionService.BASE_URL}${url}`, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', errorData);
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Request failed:', error);
      throw error;
    }
  }

  // ===== PRESCRIPTION HINTS METHODS =====
  static async searchFrequencyHints(query: string, language: string = 'en'): Promise<PrescriptionHint[]> {
    const result = await this.request(`/prescription-hints/frequencies/search?q=${encodeURIComponent(query)}&language=${language}`);
    return result.data;
  }
static async quickAddMedicine(data: QuickAddMedicineData): Promise<Medicine> {
  try {
    const result = await this.request('/medicines/quick-add', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    
    if (result.success && result.data) {
      return result.data;
    } else if (result.data) {
      return result.data;
    } else {
      return result;
    }
  } catch (error: any) {
    // Handle conflict (medicine already exists)
    if (error.message.includes('already exists')) {
      throw new Error('This medicine already exists in the database');
    }
    console.error('Quick add medicine error:', error);
    throw error;
  }
}
  static async checkSimilarMedicines(name: string): Promise<Medicine[]> {
    try {
      const results = await this.searchMedicines(name);
      return results;
    } catch (error) {
      console.error('Check similar medicines error:', error);
      return [];
    }
  }
  static async createFrequencyHint(hintText: string, language: string = 'en'): Promise<{ hint: PrescriptionHint; existed: boolean }> {
    const result = await this.request('/prescription-hints/frequencies', {
      method: 'POST',
      body: JSON.stringify({ 
        hint_text: hintText,
        language: language,
        category: 'frequency'
      })
    });
    return {
      hint: result.data,
      existed: result.data.existed || false
    };
  }

  static async searchDosageHints(query: string, language: string = 'en'): Promise<PrescriptionHint[]> {
    const result = await this.request(`/prescription-hints/search/dosage?q=${encodeURIComponent(query)}&language=${language}`);
    return result.data;
  }

  static async searchInstructionHints(query: string, language: string = 'en'): Promise<PrescriptionHint[]> {
    const result = await this.request(`/prescription-hints/search/instructions?q=${encodeURIComponent(query)}&language=${language}`);
    return result.data;
  }

  static async createHintByCategory(hintText: string, category: string, language: string = 'en'): Promise<{ hint: PrescriptionHint; existed: boolean }> {
    const result = await this.request('/prescription-hints', {
      method: 'POST',
      body: JSON.stringify({ 
        hint_text: hintText,
        language: language,
        category: category
      })
    });
    return {
      hint: result.data,
      existed: result.data.existed || false
    };
  }

  // ===== MEDICINES WITH DRUG DETAILS =====
  static async searchMedicines(query: string): Promise<Medicine[]> {
    try {
      const result = await this.request(`/medicines/search?q=${encodeURIComponent(query)}`);
      
      if (result.success && result.data) {
        return Array.isArray(result.data) ? result.data : [];
      } else if (result.data && Array.isArray(result.data)) {
        return result.data;
      } else if (Array.isArray(result)) {
        return result;
      } else {
        console.warn('Unexpected search response structure:', result);
        return [];
      }
    } catch (error) {
      console.error('Search medicines error:', error);
      throw error;
    }
  }

  static async getMedicines(params?: MedicineSearchParams): Promise<LaravelPaginatedResponse<Medicine>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const url = `/medicines${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await this.request(url);
  }

  static async getMedicine(id: number): Promise<Medicine> {
    const result = await this.request(`/medicines/${id}`);
    return result.data;
  }

  static async getMedicineAlternatives(id: number): Promise<Medicine[]> {
    const result = await this.request(`/medicines/${id}/alternatives`);
    return result.data;
  }

  static async getMedicinesWithWarnings(): Promise<Medicine[]> {
    const result = await this.request('/medicines/with-warnings');
    return result.data;
  }

  static async getMedicineCategories(): Promise<string[]> {
    const result = await this.request('/medicines/categories');
    return result.data;
  }

  static async getMedicineForms(): Promise<string[]> {
    const result = await this.request('/medicines/forms');
    return result.data;
  }

  static async getDrugTypes(): Promise<string[]> {
    const result = await this.request('/medicines/drug-types');
    return result.data;
  }

  static async getMedicineStatistics(): Promise<any> {
    const result = await this.request('/medicines/statistics');
    return result.data;
  }

  // ===== PRESCRIPTIONS =====
  static async createPrescription(data: CreatePrescriptionData): Promise<Prescription> {
    try {
      const result = await this.request('/prescriptions', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      if (result.success && result.data) {
        return result.data;
      } else if (result.data) {
        return result.data;
      } else {
        return result;
      }
    } catch (error) {
      console.error('Create prescription error:', error);
      throw error;
    }
  }

  static async getPrescriptions(params?: {
    patient_id?: number;
    doctor_id?: number;
    status?: 'active' | 'completed' | 'cancelled';
    date_from?: string;
    date_to?: string;
    search?: string;
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<LaravelPaginatedResponse<Prescription>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const url = `/prescriptions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await this.request(url);
  }

  
  

  // ===== PRESCRIPTION ACTIONS WITH PRINT FUNCTIONALITY =====
  
  // Generate PDF - Updated to use Laravel backend URL like lab requisitions
  // Generate PDF - Updated to match lab pattern exactly
static async generatePDF(id: number): Promise<void> {
  // Remove trailing slash from base URL to avoid double slashes
  const backendUrl = (process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api').replace(/\/$/, '');
  const printUrl = `${backendUrl}/prescriptions/${id}/print`; // Added /print
  window.open(printUrl, '_blank');
}

static openPrintView(id: number): void {
  const backendUrl = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api';
  const printUrl = `${backendUrl}/prescriptions/${id}/print`; // keep /api
  window.open(printUrl, '_blank');
}


  // Alternative method for PDF download (keeping original functionality)
  static async downloadPDF(prescriptionId: number): Promise<{ pdf_url: string; pdf_path: string }> {
    const token = localStorage.getItem('auth_token');
    
    const response = await fetch(`${this.BASE_URL}/prescriptions/${prescriptionId}/generate-pdf`, {
      method: 'POST',
      headers: {
        'Accept': 'application/pdf',
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to generate PDF');
    }

    const result = await response.json();
    return result.data;
  }

  

  static async sendWhatsApp(prescriptionId: number, message?: string): Promise<{ success: boolean; message: string; whatsapp_url?: string; phone?: string; }> {
    const result = await this.request(`/prescriptions/${prescriptionId}/send-whatsapp`, {
      method: 'POST',
      body: JSON.stringify({ message: message || 'Your prescription is ready.' })
    });
    return result.data;
  }

  static async generateAndSendPrescription(prescriptionId: number, sendWhatsApp: boolean = true): Promise<{
    [x: string]: any;
    pdf_url: string;
    pdf_path: string;
    whatsapp_sent?: boolean;
    whatsapp_url?: string;
    whatsapp_message?: string;
  }> {
    try {
      const result = await this.request(`/prescriptions/${prescriptionId}/generate-and-send`, {
        method: 'POST',
        body: JSON.stringify({ send_whatsapp: sendWhatsApp })
      });
      
      if (result.success && result.data) {
        return result.data;
      } else if (result.data) {
        return result.data;
      } else {
        return result;
      }
    } catch (error) {
      console.error('Generate and send prescription error:', error);
      throw error;
    }
  }

  // ===== PATIENT PRESCRIPTIONS =====
  static async getPatientPrescriptions(
  patientId: number, 
  sortBy: string = 'created_at',
  sortOrder: 'asc' | 'desc' = 'desc'
): Promise<Prescription[]> {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('sort_by', sortBy);
    queryParams.append('sort_order', sortOrder);
    
    const result = await this.request(
      `/patients/${patientId}/prescriptions?${queryParams.toString()}`
    );
    
    if (result.success && result.data) {
      return Array.isArray(result.data) ? result.data : [];
    } else if (result.data && Array.isArray(result.data)) {
      return result.data;
    } else if (Array.isArray(result)) {
      return result;
    } else {
      console.warn('Unexpected response structure:', result);
      return [];
    }
  } catch (error) {
    console.error('Get patient prescriptions error:', error);
    throw error;
  }
}

  // ===== DRUG SAFETY & INTERACTIONS =====
  static async checkDrugInteractions(medicineIds: number[]): Promise<DrugInteractionWarning[]> {
    const result = await this.request('/prescriptions/check-interactions', {
      method: 'POST',
      body: JSON.stringify({ medicine_ids: medicineIds })
    });
    return result.data;
  }

  static async checkPatientAllergies(patientId: number, medicineIds: number[]): Promise<{
    has_allergies: boolean;
    conflicting_medicines: Medicine[];
    warnings: string[];
  }> {
    const result = await this.request('/prescriptions/check-allergies', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId, medicine_ids: medicineIds })
    });
    return result.data;
  }

  static async getPatientMedicineHistory(patientId: number): Promise<{
    frequently_prescribed: Medicine[];
    recent_medicines: Medicine[];
    known_allergies: string[];
    previous_reactions: Array<{
      medicine_name: string;
      reaction: string;
      date: string;
    }>;
  }> {
    const result = await this.request(`/patients/${patientId}/medicine-history`);
    return result.data;
  }

  // ===== PRESCRIPTION STATISTICS =====
  static async getStatistics(): Promise<{
    total_prescriptions: number;
    active_prescriptions: number;
    completed_prescriptions: number;
    cancelled_prescriptions: number;
    today_prescriptions: number;
    this_week_prescriptions: number;
    this_month_prescriptions: number;
  }> {
    const result = await this.request('/prescriptions/statistics');
    return result.data;
  }

  // ===== UTILITY METHODS =====
  static getMedicineDisplayName(medicine: Medicine): string {
    if (medicine.display_name) {
      return medicine.display_name;
    }

    let display = medicine.name;
    if (medicine.strength) {
      display += ` ${medicine.strength}`;
    }
    if (medicine.form && medicine.form !== 'tablet') {
      display += ` (${medicine.form})`;
    }
    return display;
  }

  static formatMedicineDetails(medicine: Medicine): string {
    const parts = [];
    if (medicine.generic_name && medicine.generic_name !== medicine.name) {
      parts.push(`Generic: ${medicine.generic_name}`);
    }
    if (medicine.manufacturer) {
      parts.push(`Mfg: ${medicine.manufacturer}`);
    }
    if (medicine.drug_details?.laboratory) {
      parts.push(`Lab: ${medicine.drug_details.laboratory}`);
    }
    if (medicine.category) {
      parts.push(`Category: ${medicine.category}`);
    }
    if (medicine.drug_details?.drug_type) {
      parts.push(`Type: ${medicine.drug_details.drug_type}`);
    }
    return parts.join(' • ');
  }

  static formatDrugDetails(drugDetails: DrugDetails): string {
    const parts = [];
    if (drugDetails.drug_number) {
      parts.push(`Drug #: ${drugDetails.drug_number}`);
    }
    if (drugDetails.laboratory) {
      parts.push(`Lab: ${drugDetails.laboratory}`);
    }
    if (drugDetails.country_of_origin) {
      parts.push(`Origin: ${drugDetails.country_of_origin}`);
    }
    if (drugDetails.drug_type) {
      parts.push(`Type: ${drugDetails.drug_type}`);
    }
    return parts.join(' • ');
  }

  static hasSafetyWarnings(medicine: Medicine): boolean {
    return medicine.drug_details?.has_safety_warnings || 
           !!(medicine.drug_details?.warnings || medicine.drug_details?.drug_interactions);
  }

  static getSafetyWarningsText(medicine: Medicine): string[] {
    const warnings = [];
    if (medicine.drug_details?.warnings) {
      warnings.push(medicine.drug_details.warnings);
    }
    if (medicine.drug_details?.drug_interactions) {
      warnings.push(`Drug Interactions: ${medicine.drug_details.drug_interactions}`);
    }
    if (medicine.drug_details?.common_side_effects) {
      warnings.push(`Common Side Effects: ${medicine.drug_details.common_side_effects}`);
    }
    return warnings;
  }

  static getAlternativeMedicines(medicine: Medicine): string[] {
    return medicine.drug_details?.alternatives || [];
  }

  static getDrugBrands(medicine: Medicine): string[] {
    return medicine.drug_details?.drug_brands || [];
  }

  static getFrequencyDisplayText(frequency: string): string {
    const frequencyMap: { [key: string]: string } = {
      'once_daily': 'Once daily',
      'twice_daily': 'Twice daily (BID)',
      'three_times': 'Three times daily (TID)',
      'four_times': 'Four times daily (QID)',
      'every_4_hours': 'Every 4 hours',
      'every_6_hours': 'Every 6 hours',
      'every_8_hours': 'Every 8 hours',
      'every_12_hours': 'Every 12 hours',
      'as_needed': 'As needed (PRN)',
      'before_sleep': 'Before sleep',
      'with_meals': 'With meals'
    };
    return frequencyMap[frequency] || frequency || 'Not specified';
  }

  static getDurationDisplayText(duration: string): string {
    const durationMap: { [key: string]: string } = {
      '3_days': '3 days | ٣ أيام',
      '5_days': '5 days | ٥ أيام',
      '7_days': '1 week | أسبوع',
      '10_days': '10 days | ١٠ أيام',
      '14_days': '2 weeks | أسبوعان',
      '21_days': '3 weeks | ٣ أسابيع',
      '30_days': '1 month | شهر واحد',
      '60_days': '2 months | شهران',
      '90_days': '3 months | ٣ أشهر',
      'ongoing': 'Ongoing | مستمر',
      'as_needed': 'As needed | حسب الحاجة'
    };
    return durationMap[duration] || duration;
  }

  static getMealTimingDisplayText(timing: 'before' | 'after' | 'with' | 'anytime'): string {
    const timingMap = {
      'before': 'Before meals',
      'after': 'After meals',
      'with': 'With meals',
      'anytime': 'Anytime'
    };
    return timingMap[timing];
  }

  // Helper methods for status and priority (like lab requisitions)
  static getStatusColor(status: string): string {
    switch (status) {
      case 'active': return '#10b981';
      case 'completed': return '#3b82f6';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  }

  static getStatusLabel(status: string): string {
    switch (status) {
      case 'active': return 'Active';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return 'Unknown';
    }
  }

  static getStatusIcon(status: string): string {
    switch (status) {
      case 'active': return '✅';
      case 'completed': return '📋';
      case 'cancelled': return '❌';
      default: return '📋';
    }
  }

  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  }

  static formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  static formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // ===== PRESCRIPTION TEMPLATES & SUGGESTIONS =====
  static async getPrescriptionTemplates(): Promise<Array<{
    id: number;
    name: string;
    description: string;
    medicines: Medicine[];
    category: string;
  }>> {
    const result = await this.request('/prescriptions/templates');
    return result.data;
  }

  static async getMedicineSuggestions(diagnosis: string, patientAge?: number, patientGender?: 'male' | 'female'): Promise<Medicine[]> {
    const result = await this.request('/prescriptions/suggestions', {
      method: 'POST',
      body: JSON.stringify({ 
        diagnosis, 
        patient_age: patientAge, 
        patient_gender: patientGender 
      })
    });
    return result.data;
  }

  // ===== PRESCRIPTION ANALYTICS =====
  static async getPrescriptionStatistics(dateFrom?: string, dateTo?: string): Promise<{
    total_prescriptions: number;
    active_prescriptions: number;
    most_prescribed_medicines: Array<{
      medicine: Medicine;
      count: number;
    }>;
    prescription_trends: Array<{
      date: string;
      count: number;
    }>;
  }> {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);

    const result = await this.request(`/prescriptions/statistics?${params.toString()}`);
    return result.data;
  }
  static async getPrescription(prescriptionId: number): Promise<Prescription> {
  const result = await this.request(`/prescriptions/${prescriptionId}`);
  return result.data;
}

/**
 * Update an existing prescription
 */
static async updatePrescription(
  prescriptionId: number,
  data: Partial<CreatePrescriptionData>
): Promise<Prescription> {
  const result = await this.request(`/prescriptions/${prescriptionId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
  
  if (result.warnings) {
    console.warn('Prescription updated with warnings:', result.warnings);
  }
  
  return result.data;
}

/**
 * Delete a prescription
 */
static async deletePrescription(prescriptionId: number): Promise<void> {
  await this.request(`/prescriptions/${prescriptionId}`, {
    method: 'DELETE'
  });
}

/**
 * Update prescription status
 */
static async updatePrescriptionStatus(
  prescriptionId: number,
  status: 'active' | 'completed' | 'cancelled'
): Promise<Prescription> {
  const result = await this.request(`/prescriptions/${prescriptionId}`, {
    method: 'PUT',
    body: JSON.stringify({ status })
  });
  return result.data;
}
}

export default PrescriptionService;